import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from "react-router";
import BoardView from './components/BoardView'
import ListView from './components/ListView'
import DocumentsView from './components/DocumentsView'
import UploadDocumentsModal from './components/DocumentsView/UploadDocumentsModal'
import OvalSwitcher from '../../components/OvalSwitcher'
import BoardViewIcon from '../../assets/icon/BoardViewIcon'
import ListViewIcon from '../../assets/icon/ListViewIcon'
import DocumentsIcon from '../../assets/icon/DocumentsIcon'
import Header from '../../components/Header'
import styles from './index.module.scss'
import TaskModal from "../../components/Modal/components/TaskModal"
import ProjectService from "../../services/project.service";
import DocumentService from '../../services/document.service'
import { Project as ProjectDTO, ProjectDocument } from "../../types";
import TaskService from "../../services/task.service";
import DropDown from "../../components/DropDown";
import DotsVerticalIcon from "../../assets/icon/DotsVerticalIcon";
import CopyLinkIcon from "../../assets/icon/CopyLinkIcon";
import EditIcon from "../../assets/icon/EditIcon";
import TrashIcon from "../../assets/icon/TrashIcon";
import ProjectDuplicateIcon from "../../assets/icon/ProjectDuplicateIcon";
import ProjectStatusesIcon from "../../assets/icon/ProjectStatusesIcon";
import PrivacySettingsIcon from '../../assets/icon/PrivacySettingsIcon';
import UsersIcon from "../../assets/icon/UsersIcon";
import PlusIcon from "../../assets/icon/PlusIcon";
import ArchiveIcon from '../../assets/icon/ArchiveIcon';
import MyDocumentsIcon from '../../assets/icon/MyDocumentsIcon';
import FilterCloseIcon from '../../assets/icon/FilterCloseIcon';
import FilterCloseGrayIcon from '../../assets/icon/FilterCloseGrayIcon';
import FiltersIcon from '../../assets/icon/FiltersIcon';
import SuccessNotificationIcon from '../../assets/icon/SuccessNotificationIcon';
import ErrorNotificationIcon from '../../assets/icon/ErrorNotificationIcon';
import { PROJECT_NOTIFICATION_EVENT, ProjectNotificationDetail, dispatchProjectNotification } from '../../utils/projectNotification';
import ProjectStatusModal from '../../components/ProjectControls/ProjectStatusModal';
import ProjectDeleteModal from '../../components/ProjectControls/ProjectDeleteModal';
import ProjectDuplicateModal from '../../components/ProjectControls/ProjectDuplicateModal';
import ShareProjectModal from '../../components/ShareProjectModal';
import ProjectMembersPanel, { type ProjectMembersPanelSelectionMember } from '../../components/ProjectMembersPanel';
import UsersAvatar from '../../components/UsersAvatar';
import ChevronDownIcon from '../../components/icons/ChevronDownIcon';
import TaskFilters from './components/TaskFilters';
import { ProjectTaskStatusItem, toProjectBoardStatuses } from '../../utils/projectStatus';
import { copyProjectLink } from '../../utils/projectLink';
import { persistRelatedTask } from '../../utils/persistRelatedTask';
import { openProjectDocument } from '../../utils/projectDocument';
import { useUser } from 'cor-business';
import { useWorkspace } from '../../context/workspace.context';
import { ensureWorkspaceForEntity, getProjectWorkspaceId } from '../../utils/workspaceNavigation';
import AttachmentService from "../../services/attachment.service";
import TimeLogService from "../../services/time-log.service";
import { UserService } from '../../services';
import WorkspaceMembersService from '../../services/workspace-members.service';
import { extractList, getProjectMemberUserId, getWorkspaceMemberUserId, normalizeProjectMember } from '../../utils/members';
import {
    extractTaskList,
    filterArchivedTasks,
    isArchivedTask,
    mergeTasks,
    removeTaskById,
    upsertTask,
} from '../../utils/taskList';
import { mergeOrderedTaskSubset } from './hooks/useTaskDragAndDrop';
import { useTaskFilters } from './hooks/useTaskFilters';
import { readProjectTaskOrder, sortTasksBySavedOrder, writeProjectTaskOrder } from './utils/taskOrder';
import { stripTaskTimeTrackingFields } from './utils/taskPayload';
import {
    findProjectUser,
    getTaskAssigneeIds,
    getUserDisplayName,
    isTaskAssignedToCurrentUser,
    taskMatchesAssigneeFilter,
} from './utils/taskUsers';
import {
    applyTaskFilters,
    EMPTY_TASK_FILTER_STATE,
    hasTaskFilterValue,
} from './utils/taskFilters';
import {
    PendingInlineImage,
    persistPendingInlineImages,
    removePendingInlineImages,
} from '../../utils/inlineTaskImages';

const viewSwitcherItems = [
    {
        id: 'board',
        title: 'Доска',
        icon: <BoardViewIcon />
    },
    {
        id: 'list',
        title: 'Список',
        icon: <ListViewIcon />
    },
    {
        id: 'documents',
        title: 'Документы',
        icon: <DocumentsIcon />
    },
]

function isArchivedDocument(document: ProjectDocument) {
    return Boolean(document?.is_archived || document?.archived_at || document?.status === 'archived')
}

function mergeDocuments(...documentLists: ProjectDocument[][]) {
    const documentMap = new Map<string, ProjectDocument>()

    documentLists.flat().forEach((document) => {
        if (document?.id == null) {
            return
        }

        documentMap.set(String(document.id), document)
    })

    return Array.from(documentMap.values())
}

function removeDocumentById(documentList: ProjectDocument[], documentId?: string | number | null) {
    if (documentId == null) {
        return documentList
    }

    return documentList.filter((document) => String(document.id) !== String(documentId))
}

// Иконка + подпись + крестик сброса — одинаковые у переключателей «Мои» и
// «Архивированные», которые работают и для задач, и для документов.
function ToggleFilterButton({ icon, label, isActive, onToggle }: {
    icon: React.ReactNode
    label: string
    isActive: boolean
    onToggle: () => void
}) {
    return (
        <button
            type="button"
            className={`${styles.project__filterBtn} ${isActive ? styles.project__filterBtnActive : ''}`}
            onClick={onToggle}
        >
            <span className={styles.project__filterIcon}>
                {icon}
            </span>
            <span>{label}</span>
            {isActive && (
                <span className={styles.project__filterClose}>
                    <FilterCloseIcon />
                </span>
            )}
        </button>
    )
}

function buildProjectUsers(projectMembersData: any, workspaceMembersData: any) {
    const workspaceMembersByUser = new Map<string, any>()

    extractList(workspaceMembersData).forEach((member) => {
        const userId = getWorkspaceMemberUserId(member)

        if (userId) {
            workspaceMembersByUser.set(userId, member)
        }
    })

    return extractList(projectMembersData)
        .map((projectMember) => normalizeProjectMember(projectMember, workspaceMembersByUser.get(getProjectMemberUserId(projectMember))))
        .filter(Boolean)
}

export default function Project() {
    const { projectId } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const { user } = useUser()
    const { workspace, workspaces, setWorkspace, reloadWorkspaces } = useWorkspace()
    const [project, setProject] = useState<ProjectDTO | null | any>(null)
    const [isProjectLoading, setIsProjectLoading] = useState(true)
    const [projectAccessError, setProjectAccessError] = useState<string | null>(null)
    // Актуальные значения для эффекта переключения пространства: нужны, чтобы эффект
    // не перезапускался на каждое обновление списка пространств.
    const workspaceRef = useRef(workspace)
    const workspacesRef = useRef(workspaces)

    workspaceRef.current = workspace
    workspacesRef.current = workspaces
    const [tasks, setTasks] = useState<any[]>([])
    // Project whose task list has finished loading — a task link is resolved
    // only after that, otherwise it opens without its stages.
    const [loadedTasksProjectId, setLoadedTasksProjectId] = useState<string | null>(null)
    const [projectUsers, setProjectUsers] = useState<any[]>([])
    const [documents, setDocuments] = useState<ProjectDocument[]>([])
    const [activeView, setActiveView] = useState('board')
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
    const [linkedTask, setLinkedTask] = useState<any | null>(null)
    const [isUploadDocumentsOpen, setIsUploadDocumentsOpen] = useState(false)
    const [isRenaming, setIsRenaming] = useState(false)
    const [renameName, setRenameName] = useState('')
    const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false)
    const [isMembersPanelOpen, setIsMembersPanelOpen] = useState(false)
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
    const [statusModalStatuses, setStatusModalStatuses] = useState<ProjectTaskStatusItem[]>([])
    const [isDocumentsLoading, setIsDocumentsLoading] = useState(false)
    const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(false)
    const [showArchivedTasks, setShowArchivedTasks] = useState(false)
    const [showOnlyMyDocuments, setShowOnlyMyDocuments] = useState(false)
    const [showArchivedDocuments, setShowArchivedDocuments] = useState(false)
    const {
        isTaskFiltersOpen,
        removeUnavailableAssignees,
        selectedAssigneeIds,
        setIsTaskFiltersOpen,
        taskFilterState,
        updateSelectedAssigneeIds,
        updateTaskFilters,
    } = useTaskFilters(projectId)
    const [successNotification, setSuccessNotification] = useState<{ title: string, text: string, type?: 'success' | 'error', action?: { label: string, onClick: () => void } } | null>(null)
    const renameInputRef = useRef<HTMLInputElement>(null)
    const renameSaveInFlightRef = useRef(false)
    const skipRenameBlurRef = useRef(false)
    const handledLinkedDocumentRef = useRef<string | null>(null)
    const handledLinkedTaskRef = useRef<string | null>(null)
    const projectUsersProjectIdRef = useRef<string | null>(null)
    const locationSearchParams = new URLSearchParams(location.search)
    const requestedView = locationSearchParams.get('view')
    const linkedTaskId = locationSearchParams.get('task')
    const linkedCommentId = locationSearchParams.get('comment_id')
    const linkedDocumentId = locationSearchParams.get('document')
    const linkedAction = locationSearchParams.get('action')
    const currentUserIds = useMemo(() => (
        [
            user?.user_id,
            user?.id,
            user?.profile?.user_id,
            user?.profile?.id,
        ].filter(Boolean)
    ), [user])
    const currentUserEmails = useMemo(() => (
        [
            user?.email,
            user?.profile?.email,
        ].filter(Boolean)
    ), [user])

    useEffect(() => {
        let isMounted = true

        const loadProjectPage = async () => {
            if (!projectId) return

            setProject(null)
            setTasks([])
            setProjectUsers([])
            setDocuments([])
            setProjectAccessError(null)
            setIsProjectLoading(true)

            try {
                const [currentProject, currentStatuses] = await Promise.all([
                    ProjectService.get(projectId),
                    ProjectService.getSortedTaskStatuses(projectId),
                ])

                if (!isMounted) return

                setProject({
                    ...currentProject,
                    statuses: toProjectBoardStatuses(currentStatuses),
                })
            } catch (error: any) {
                if (!isMounted) return

                console.error('Failed to load project:', error)
                setProject(null)
                setProjectAccessError(
                    error?.status === 403 || error?.status === 401
                        ? 'У вас нет доступа к этому проекту.'
                        : error?.status === 404
                            ? 'Проект не найден или был удалён.'
                            : 'Не удалось загрузить проект. Попробуйте обновить страницу.'
                )
            } finally {
                if (isMounted) {
                    setIsProjectLoading(false)
                }
            }
        }

        loadProjectPage()

        return () => {
            isMounted = false
        }
    }, [projectId])

    // Ссылку на проект могли открыть, находясь в другом рабочем пространстве
    // (например, вставили её из мессенджера). Тогда переключаем пространство на то,
    // которому принадлежит проект, иначе остальные экраны показывают чужие данные.
    const projectWorkspaceId = getProjectWorkspaceId(project)

    useEffect(() => {
        if (projectWorkspaceId == null) {
            return
        }

        if (String(projectWorkspaceId) === String(workspace?.id)) {
            setProjectAccessError(null)
            return
        }

        let isMounted = true

        void (async () => {
            try {
                const result = await ensureWorkspaceForEntity({
                    workspaceId: projectWorkspaceId,
                    currentWorkspace: workspaceRef.current,
                    workspaces: workspacesRef.current,
                    setWorkspace,
                    fetchWorkspaces: reloadWorkspaces,
                })

                if (isMounted) {
                    setProjectAccessError(
                        result === 'not-found'
                            ? 'Проект находится в рабочем пространстве, к которому у вас нет доступа.'
                            : null
                    )
                }
            } catch (error) {
                console.error('Failed to reload workspaces for project:', error)

                if (isMounted) {
                    setProjectAccessError('Не удалось проверить доступ к рабочему пространству проекта.')
                }
            }
        })()

        return () => {
            isMounted = false
        }
    }, [projectWorkspaceId, reloadWorkspaces, setWorkspace, workspace?.id])

    const getTasks = useCallback(async () => {
        if (!projectId) return

        try {
            const [tasksList, archivedTasksList] = await Promise.all([
                ProjectService.getAllProjectTasks(projectId),
                showArchivedTasks
                    ? ProjectService.listArchivedTasks(projectId).catch(() => [])
                    : Promise.resolve([]),
            ])

            const visibleTasks = extractTaskList(tasksList)
            const archivedTasks = extractTaskList(archivedTasksList)

            setTasks((prev) => {
                return sortTasksBySavedOrder(mergeTasks(
                    filterArchivedTasks(visibleTasks),
                    prev.filter((task) => isArchivedTask(task)),
                    archivedTasks,
                ), readProjectTaskOrder(projectId))
            })
        } catch (error) {
            console.error('Failed to fetch tasks:', error)
            setTasks([])
        } finally {
            setLoadedTasksProjectId(projectId)
        }
    }, [projectId, showArchivedTasks])

    const getDocuments = useCallback(async () => {
        if (!projectId) return

        setIsDocumentsLoading(true)

        try {
            const [documentList, archivedDocumentList] = await Promise.all([
                DocumentService.list(projectId),
                showArchivedDocuments
                    ? DocumentService.listArchived(projectId).catch(() => [])
                    : Promise.resolve([]),
            ])

            setDocuments((prev) => {
                return mergeDocuments(
                    documentList,
                    prev.filter((document) => isArchivedDocument(document)),
                    archivedDocumentList,
                )
            })
        } catch (error) {
            console.error('Failed to fetch documents:', error)
            setDocuments([])
        } finally {
            setIsDocumentsLoading(false)
        }
    }, [projectId, showArchivedDocuments])

    useEffect(() => {
        if (activeView === 'documents' && projectId) {
            void getDocuments()
        }
    }, [activeView, getDocuments, projectId])

    const isTaskViewActive = activeView !== 'documents'
    useEffect(() => {
        if (isTaskViewActive && projectId) {
            void getTasks()
        }
    }, [isTaskViewActive, getTasks, projectId])

    useEffect(() => {
        if (requestedView === 'board' || requestedView === 'list' || requestedView === 'documents') {
            setActiveView(requestedView)
        }
    }, [requestedView])

    useEffect(() => {
        handledLinkedDocumentRef.current = null
    }, [linkedAction, linkedDocumentId, projectId])

    useEffect(() => {
        handledLinkedTaskRef.current = null
        setLinkedTask(null)
    }, [linkedCommentId, linkedTaskId, projectId])

    useEffect(() => {
        if (!successNotification) {
            return
        }

        const timeoutId = setTimeout(() => {
            setSuccessNotification(null)
        }, 5000)

        return () => clearTimeout(timeoutId)
    }, [successNotification])

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<ProjectNotificationDetail>).detail
            if (!detail) return
            const type = detail.type || 'success'
            setSuccessNotification({
                title: type === 'error' ? 'Ошибка' : 'Успех',
                text: detail.text,
                type,
                action: detail.action ? {
                    label: detail.action.label,
                    onClick: () => {
                        detail.action!.onClick()
                        setSuccessNotification(null)
                    },
                } : undefined,
            })
        }

        window.addEventListener(PROJECT_NOTIFICATION_EVENT, handler)
        return () => window.removeEventListener(PROJECT_NOTIFICATION_EVENT, handler)
    }, [])

    const showSuccessNotification = useCallback((text: string, action?: { label: string, onClick: () => void }) => {
        setSuccessNotification({
            title: 'Успех',
            text,
            type: 'success',
            action: action ? {
                label: action.label,
                onClick: () => {
                    action.onClick()
                    setSuccessNotification(null)
                },
            } : undefined,
        })
    }, [])

    const showErrorNotification = useCallback((text: string) => {
        setSuccessNotification({
            title: 'Ошибка',
            text,
            type: 'error',
        })
    }, [])

    useEffect(() => {
        if (
            activeView !== 'documents'
            || !projectId
            || !linkedDocumentId
            || linkedAction !== 'open'
            || !documents.length
        ) {
            return
        }

        const linkedDocument = documents.find((document) => String(document.id) === String(linkedDocumentId))

        if (!linkedDocument) {
            return
        }

        const handledKey = `${projectId}:${linkedDocumentId}:${linkedAction}`

        if (handledLinkedDocumentRef.current === handledKey) {
            return
        }

        handledLinkedDocumentRef.current = handledKey
        void openProjectDocument(projectId, linkedDocument, { downloadOnFirstOpen: true })
    }, [activeView, documents, linkedAction, linkedDocumentId, projectId])

    const areTasksLoaded = loadedTasksProjectId === projectId

    useEffect(() => {
        if (!projectId || !linkedTaskId || !areTasksLoaded) {
            return
        }

        const handledKey = `${projectId}:${linkedTaskId}:${linkedCommentId || ''}`

        if (handledLinkedTaskRef.current === handledKey) {
            return
        }

        const allTasks = tasks.flatMap((task) => [
            task,
            ...(Array.isArray(task.relatedTasks) ? task.relatedTasks.filter(Boolean) : []),
        ])
        const taskFromLink = allTasks.find((task) => String(task.id) === String(linkedTaskId))

        if (taskFromLink) {
            handledLinkedTaskRef.current = handledKey
            setLinkedTask(taskFromLink)
            return
        }

        // A search result or direct link can point to a task absent from the
        // current list (for example, an archived task or a newly added stage).
        let cancelled = false
        void TaskService.get(linkedTaskId).then((task) => {
            const taskProjectId = task?.project_id ?? task?.projectId
                ?? (typeof task?.project === 'object' ? task.project?.id : task?.project)
            if (cancelled || !task || String(taskProjectId) !== String(projectId)) {
                return
            }

            // A single task comes from the backend without its stages — attach
            // them from the project list, as the list itself does.
            const stages = allTasks.filter((currentTask) => String(currentTask.parent_id) === String(task.id))

            handledLinkedTaskRef.current = handledKey
            setLinkedTask(stages.length && !Array.isArray(task.relatedTasks)
                ? { ...task, relatedTasks: [...stages, null] }
                : task)
        }).catch((error) => {
            if (!cancelled) console.error('Failed to open linked task:', error)
        })

        return () => { cancelled = true }
    }, [areTasksLoaded, linkedCommentId, linkedTaskId, projectId, tasks])

    const startRename = () => {
        skipRenameBlurRef.current = false
        setRenameName(project?.name || '')
        setIsRenaming(true)
        window.setTimeout(() => {
            const input = renameInputRef.current
            if (!input) return

            input.focus()
            input.setSelectionRange(input.value.length, input.value.length)
        }, 0)
    }

    const cancelRename = () => {
        skipRenameBlurRef.current = true
        setRenameName(project?.name || '')
        setIsRenaming(false)
    }

    const confirmRename = async () => {
        if (!projectId || renameSaveInFlightRef.current) return

        const name = renameName.trim()
        if (!name) {
            cancelRename()
            return
        }

        if (name === String(project?.name || '').trim()) {
            cancelRename()
            return
        }

        renameSaveInFlightRef.current = true
        try {
            await ProjectService.update(projectId, { name })
            setProject((prev: any) => prev ? { ...prev, name } : prev)
            setIsRenaming(false)
        } catch (error) {
            console.error('Failed to rename project:', error)
            window.setTimeout(() => renameInputRef.current?.focus(), 0)
        } finally {
            renameSaveInFlightRef.current = false
        }
    }

    const handleDuplicate = async ({ name, color, copyContent }: { name: string, color: string, copyContent: boolean }) => {
        if (!projectId || !project) return

        try {
            const newProject = await ProjectService.duplicateProject({
                id: projectId,
                description: project.description,
                  workspace_id: project.workspace_id,
                  is_public: project.is_public,
                  color: project.color,
              }, name, undefined, {
                  color,
                  copyContent,
              })

            setIsDuplicateOpen(false)
            navigate(`/projects/${newProject.id}`)
            dispatchProjectNotification({ text: 'Проект успешно продублирован' })
        } catch (error) {
            console.error('Failed to duplicate project:', error)
        }
    }

    const handleDeleteProject = async () => {
        if (!projectId) return

        try {
            await ProjectService.delete(projectId)
            navigate('/projects')
        } catch (error) {
            console.error('Failed to delete project:', error)
        }
    }

    const handleOpenStatusModal = async () => {
        if (!projectId) return

        try {
            const statuses = await ProjectService.getSortedTaskStatuses(projectId)
            setStatusModalStatuses(statuses)
            setIsStatusModalOpen(true)
        } catch (error) {
            console.error('Failed to fetch task statuses:', error)
        }
    }

    const handleCopyProjectLink = async () => {
        if (!projectId) return

        try {
            await copyProjectLink(projectId)
            showSuccessNotification('Ссылка скопирована')
        } catch (error) {
            console.error('Failed to copy project link:', error)
        }
    }

    const handleLinkCopied = useCallback(() => {
        showSuccessNotification('Ссылка скопирована')
    }, [showSuccessNotification])

    const handleUploadSuccess = useCallback((uploadedIds?: Array<string | number>) => {
        const ids = uploadedIds || []
        const text = ids.length > 1 ? 'Документы успешно загружены' : 'Документ успешно загружен'
        showSuccessNotification(text, ids.length ? {
            label: 'Отменить',
            onClick: () => {
                void (async () => {
                    try {
                        await Promise.all(ids.map((id) => DocumentService.delete(projectId || '', id)))
                        await getDocuments()
                    } catch (error) {
                        console.error('Failed to undo document upload:', error)
                    }
                })()
            },
        } : undefined)
    }, [projectId, showSuccessNotification, getDocuments])

    const handleDocumentArchived = useCallback((document: ProjectDocument, undo: () => Promise<void>) => {
        showSuccessNotification('Документ перенесено в архив', {
            label: 'Отменить',
            onClick: () => {
                void undo()
                setSuccessNotification(null)
            },
        })
    }, [showSuccessNotification])

    const handleDocumentUpdated = useCallback((document: ProjectDocument | null, options?: { remove?: boolean }) => {
        setDocuments((prev) => {
            if (options?.remove) {
                return removeDocumentById(prev, document?.id)
            }

            if (!document) {
                return prev
            }

            return mergeDocuments(removeDocumentById(prev, document.id), [document])
        })
    }, [])

    const handleTaskUpdated = useCallback((task: any | null, options?: { remove?: boolean }) => {
        setTasks((prev) => {
            if (options?.remove) {
                return removeTaskById(prev, task?.id)
            }

            if (!task) {
                return prev
            }

            return upsertTask(prev, task)
        })
    }, [])

    const saveLinkedTask = useCallback(async (taskData: any) => {
        if (!projectId || !linkedTask?.id) {
            return
        }

        const updatedTask = await TaskService.update(linkedTask.id, stripTaskTimeTrackingFields(taskData))

        await Promise.all((taskData.deletedRelatedTaskIds || []).map((relatedTaskId: number | string) => (
            TaskService.delete(Number(relatedTaskId))
        )))

        await Promise.all((taskData.relatedTasks || []).filter(Boolean).map((currentTask: any) => (
            persistRelatedTask({
                task: currentTask,
                parentTaskId: linkedTask.id,
                projectId,
            })
        )))

        handleTaskUpdated(updatedTask)
        setLinkedTask(null)
        await getTasks()

        return updatedTask
    }, [getTasks, handleTaskUpdated, linkedTask?.id, projectId])

    const handleTasksReordered = useCallback((orderedVisibleTasks: any[]) => {
        setTasks((prev) => {
            const nextTasks = mergeOrderedTaskSubset(prev, orderedVisibleTasks)

            if (projectId) {
                writeProjectTaskOrder(projectId, nextTasks)
            }

            return nextTasks
        })
    }, [projectId])

    useEffect(() => {
        let isMounted = true

        const loadProjectUsers = async () => {
            if (!projectId) {
                return
            }

            projectUsersProjectIdRef.current = null

            try {
                const [projectMembersData, workspaceMembersData] = await Promise.all([
                    WorkspaceMembersService.getProjectMembers(Number(projectId)),
                    workspace?.id ? WorkspaceMembersService.getMembers(Number(workspace.id)).catch(() => null) : Promise.resolve(null),
                ])
                const projectMemberUsers = buildProjectUsers(projectMembersData, workspaceMembersData)

                if (!isMounted) {
                    return
                }

                projectUsersProjectIdRef.current = projectId
                setProjectUsers(projectMemberUsers)
            } catch (error) {
                try {
                    const usersResponse = await UserService.getUsers({
                        project_id: Number(projectId),
                        limit: 1000,
                    })

                    if (isMounted) {
                        projectUsersProjectIdRef.current = projectId
                        setProjectUsers(extractList(usersResponse))
                    }
                } catch (fallbackError) {
                    if (isMounted) {
                        console.error('Failed to fetch project users:', fallbackError)
                        setProjectUsers([])
                    }
                }
            }
        }

        void loadProjectUsers()

        return () => {
            isMounted = false
        }
    }, [projectId, workspace?.id])

    const selectionMembers = useMemo<ProjectMembersPanelSelectionMember[]>(() => (
        projectUsers.map((projectUser: any) => {
            const userId = String(projectUser?.user_id ?? projectUser?.id ?? '')

            return {
                id: userId,
                userId,
                name: getUserDisplayName(projectUser) || projectUser?.email || userId,
                email: projectUser?.email || '',
                photo: projectUser?.photo || projectUser?.avatar || '',
            }
        }).filter((member) => Boolean(member.userId))
    ), [projectUsers])

    const selectedAssigneeUsers = useMemo(() => {
        const selectedIds = new Set(selectedAssigneeIds)
        return selectionMembers.filter((member) => selectedIds.has(member.userId))
    }, [selectedAssigneeIds, selectionMembers])

    useEffect(() => {
        if (projectUsersProjectIdRef.current !== projectId || selectionMembers.length === 0) return

        removeUnavailableAssignees(selectionMembers.map((member) => member.userId))
    }, [projectId, removeUnavailableAssignees, selectionMembers])

    const currentProjectUser = useMemo(() => findProjectUser(projectUsers, {
        ids: currentUserIds,
        emails: currentUserEmails,
    }), [currentUserEmails, currentUserIds, projectUsers])
    const currentAssigneeIds = useMemo(() => Array.from(new Set([
        ...currentUserIds.map(String),
        ...getTaskAssigneeIds({ users: currentProjectUser ? [currentProjectUser] : [] }),
    ])), [currentProjectUser, currentUserIds])

    const filteredTasks = useMemo(() => {
        const taskSubset = tasks.filter((task) => {
            const shouldShowArchivedOnly = showArchivedTasks && !showOnlyMyTasks

            if (shouldShowArchivedOnly && !isArchivedTask(task)) {
                return false
            }

            if (!showArchivedTasks && isArchivedTask(task)) {
                return false
            }

            if (showOnlyMyTasks && !isTaskAssignedToCurrentUser(task, currentAssigneeIds, currentUserEmails)) {
                return false
            }

            if (!taskMatchesAssigneeFilter(task, selectedAssigneeIds)) {
                return false
            }

            return true
        })
        return applyTaskFilters(taskSubset, taskFilterState)
    }, [currentAssigneeIds, currentUserEmails, selectedAssigneeIds, showArchivedTasks, showOnlyMyTasks, taskFilterState, tasks])

    const isDocumentsView = activeView === 'documents'
    const activeTaskFilterCount = taskFilterState.rules.filter((rule) => rule.field && hasTaskFilterValue(rule)).length
    const canPersistTaskOrder = !showOnlyMyTasks
        && !showArchivedTasks
        && selectedAssigneeIds.length === 0
        && activeTaskFilterCount === 0
    // The board merges the visible order into the full task list, so tasks can
    // be moved between columns with filters too — except in the archive view.
    const canMoveBoardTasks = !showArchivedTasks

    const createTask = async (task: any) => {
        const isCreate = !task?.id
        const {
            assignee_files: assigneeFiles = [],
            inline_images: inlineImages = [],
            deletedRelatedTaskIds = [],
            relatedTasks = [],
            ...taskFields
        } = task
        const descriptionForCreate = inlineImages.length
            ? removePendingInlineImages(taskFields.description || '')
            : taskFields.description
        const newTask = await TaskService.create(stripTaskTimeTrackingFields({
            ...taskFields,
            description: descriptionForCreate,
            priority: taskFields.priority || 'medium',
            project_id: projectId
        }))

        await TimeLogService.syncForTask({
            taskId: newTask.id,
            spendTime: task.spendTime,
            description: task.timeLogDescription || '',
            timeLogId: task.timeLogId,
        })

        if(assigneeFiles.length){
            const uploadResults = await Promise.allSettled(
                assigneeFiles.map((file: any) => AttachmentService.upload(newTask.id, file))
            )
            const failedUpload = uploadResults.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined

            if (failedUpload) {
                if (isCreate && newTask?.id) {
                    try {
                        await TaskService.delete(newTask.id)
                    } catch (rollbackError) {
                        console.error('Failed to roll back task after attachment upload error:', rollbackError)
                    }
                }

                throw failedUpload.reason
            }
        }

        if (inlineImages.length) {
            try {
                const resolvedDescription = await persistPendingInlineImages(
                    Number(newTask.id),
                    taskFields.description || '',
                    inlineImages as PendingInlineImage[],
                )
                await TaskService.update(newTask.id, stripTaskTimeTrackingFields({
                    ...taskFields,
                    description: resolvedDescription,
                    priority: taskFields.priority || 'medium',
                    project_id: projectId,
                }))
                newTask.description = resolvedDescription
            } catch (error) {
                if (isCreate && newTask?.id) {
                    try {
                        await TaskService.delete(newTask.id)
                    } catch (rollbackError) {
                        console.error('Failed to roll back task after inline image upload error:', rollbackError)
                    }
                }
                throw error
            }
        }

        await Promise.all(deletedRelatedTaskIds.map((relatedTaskId: number | string) => (
            TaskService.delete(Number(relatedTaskId))
        )))

        await Promise.all(relatedTasks.filter(Boolean).map((currentTask: any) => (
            persistRelatedTask({
                task: currentTask,
                parentTaskId: newTask.id,
                projectId: projectId as string,
            })
        )))

        await getTasks()
        setIsTaskModalOpen(false)

        if (isCreate && newTask?.id) {
            showSuccessNotification('Задача успешно создана', {
                label: 'Отменить',
                onClick: () => {
                    void (async () => {
                        try {
                            await TaskService.delete(newTask.id)
                            await getTasks()
                        } catch (error) {
                            console.error('Failed to undo task creation:', error)
                        }
                    })()
                },
            })
        }

        return newTask
    }

    // Пока проект грузится (в том числе при переходе по внешней ссылке с
    // переключением пространства) показываем состояние загрузки, а не пустой экран.
    if (!project || projectAccessError) {
        return (
            <>
                <Header />
                <div className={styles.project}>
                    <div className={styles.project__stateScreen}>
                        {isProjectLoading ? (
                            <span className={styles.project__stateTitle}>Загружаем проект…</span>
                        ) : (
                            <>
                                <span className={styles.project__stateTitle}>Проект недоступен</span>
                                <span className={styles.project__stateText}>
                                    {projectAccessError || 'Не удалось открыть проект по этой ссылке.'}
                                </span>
                                <button
                                    type="button"
                                    className={styles.project__stateAction}
                                    onClick={() => navigate('/projects')}
                                >
                                    К списку проектов
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <Header />
            <div className={styles.project}>
                <div className={styles.project__titleBar}>
                    <div className={styles.project__titleLeft}>
                        <div className={styles.project__breadcrumbs}>
                            <span className={styles.project__breadcrumbsLink} onClick={() => navigate('/workspaces')}>
                                {workspace?.name || 'Рабочее пространство'}
                            </span>
                            <span className={styles.project__breadcrumbsSeparator}>/</span>
                            <span className={styles.project__breadcrumbsLink} onClick={() => navigate('/projects')}>
                                Проекты
                            </span>
                            <span className={styles.project__breadcrumbsSeparator}>/</span>
                        </div>
                        {isRenaming ? (
                            <div className={styles.project__renameWrap}>
                                <input
                                    ref={renameInputRef}
                                    className={styles.project__renameInput}
                                    value={renameName}
                                    aria-label="Название проекта"
                                    onChange={(e) => setRenameName(e.target.value)}
                                    onBlur={() => {
                                        if (skipRenameBlurRef.current) {
                                            skipRenameBlurRef.current = false
                                            return
                                        }

                                        void confirmRename()
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            e.currentTarget.blur()
                                        }
                                        if (e.key === 'Escape') {
                                            e.preventDefault()
                                            cancelRename()
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <span className={styles.project__backTitle}>{project.name || 'Проект'}</span>
                        )}
                        <DropDown
                            closeDropDownByPick
                            btn={
                                <div className={styles.project__dotsBtn}>
                                    <DotsVerticalIcon />
                                </div>
                            }
                        >
                            <div className={styles.project__menu}>
                                <div className={styles.project__menuItem} onClick={startRename}>
                                    <EditIcon />
                                    <span>Переименовать</span>
                                </div>
                                <div className={styles.project__menuItem} onClick={() => setIsDuplicateOpen(true)}>
                                    <ProjectDuplicateIcon />
                                    <span>Дублировать</span>
                                </div>
                                <div className={styles.project__menuItem} onClick={handleCopyProjectLink}>
                                    <CopyLinkIcon />
                                    <span>Копировать ссылку</span>
                                </div>
                                <div className={styles.project__menuItem} onClick={() => setIsPrivacyOpen(true)}>
                                    <PrivacySettingsIcon />
                                    <span>Настройки приватности</span>
                                </div>
                                <div className={styles.project__menuItem} onClick={handleOpenStatusModal}>
                                    <ProjectStatusesIcon />
                                    <span>Настройка статусов</span>
                                </div>
                                <div className={`${styles.project__menuItem} ${styles.project__menuItemDanger}`} onClick={() => setIsDeleteOpen(true)}>
                                    <TrashIcon />
                                    <span>Удалить</span>
                                </div>
                            </div>
                        </DropDown>
                    </div>
                    <div className={styles.project__actions}>
                        <div className={styles.project__filters}>
                            {!isDocumentsView && (
                                <DropDown
                                    isOpen={isTaskFiltersOpen}
                                    setIsOpen={(isOpen) => {
                                        setIsTaskFiltersOpen(isOpen)
                                        if (isOpen) setIsMembersPanelOpen(false)
                                    }}
                                    position="right"
                                    verticalPlacement="bottom"
                                    dropdownClassName={styles.project__taskFiltersDropdown}
                                    contentClassName={styles.project__taskFiltersDropdownContent}
                                    btn={(isOpen) => (
                                        <div className={styles.project__filterControl}>
                                            <button
                                                type="button"
                                                className={`${styles.project__filterBtn} ${(isOpen || activeTaskFilterCount > 0) ? styles.project__filterBtnActive : ''}`}
                                                aria-expanded={isOpen}
                                            >
                                                <span className={styles.project__filterIcon} aria-hidden="true">
                                                    <FiltersIcon />
                                                </span>
                                                <span>{activeTaskFilterCount > 0 ? `Фильтры: ${activeTaskFilterCount}` : 'Фильтры'}</span>
                                                {activeTaskFilterCount === 0 && (
                                                    <span className={`${styles.project__filterChevron} ${isOpen ? styles.project__filterChevronOpen : ''}`} aria-hidden="true">
                                                        <ChevronDownIcon />
                                                    </span>
                                                )}
                                            </button>
                                            {activeTaskFilterCount > 0 ? (
                                                <button
                                                    type="button"
                                                    className={styles.project__filterReset}
                                                    aria-label="Сбросить фильтры"
                                                    onClick={(event) => {
                                                        event.stopPropagation()
                                                        updateTaskFilters(EMPTY_TASK_FILTER_STATE)
                                                    }}
                                                >
                                                    <FilterCloseIcon />
                                                </button>
                                            ) : null}
                                        </div>
                                    )}
                                >
                                    <TaskFilters
                                        state={taskFilterState}
                                        members={selectionMembers}
                                        statuses={project?.statuses || []}
                                        onChange={updateTaskFilters}
                                        onClose={() => setIsTaskFiltersOpen(false)}
                                    />
                                </DropDown>
                            )}
                            <div className={styles.project__filterControl}>
                                <button
                                    type="button"
                                    className={`${styles.project__filterBtn} ${(isMembersPanelOpen || selectedAssigneeIds.length > 0) ? styles.project__filterBtnActive : ''}`}
                                    aria-expanded={isMembersPanelOpen}
                                    onClick={() => {
                                        setIsTaskFiltersOpen(false)
                                        setIsMembersPanelOpen((isOpen) => !isOpen)
                                    }}
                                >
                                    <span className={styles.project__filterIcon} aria-hidden="true">
                                        <UsersIcon />
                                    </span>
                                    {selectedAssigneeUsers.length > 0 ? (
                                        <span className={styles.project__filterAvatars}>
                                            <UsersAvatar users={selectedAssigneeUsers} avatarsCount={4} compact />
                                        </span>
                                    ) : (
                                        <span>Участники</span>
                                    )}
                                </button>
                                {selectedAssigneeIds.length > 0 && (
                                    <button
                                        type="button"
                                        className={styles.project__filterReset}
                                        aria-label="Сбросить фильтр по участникам"
                                        onClick={() => {
                                            updateSelectedAssigneeIds([])
                                        }}
                                    >
                                        <FilterCloseGrayIcon />
                                    </button>
                                )}
                            </div>
                            <ToggleFilterButton
                                icon={<MyDocumentsIcon />}
                                label="Мои"
                                isActive={isDocumentsView ? showOnlyMyDocuments : showOnlyMyTasks}
                                onToggle={() => {
                                    const setShowOnlyMine = isDocumentsView ? setShowOnlyMyDocuments : setShowOnlyMyTasks
                                    setShowOnlyMine((prev) => !prev)
                                }}
                            />
                            <ToggleFilterButton
                                icon={<ArchiveIcon />}
                                label="Архивированные"
                                isActive={isDocumentsView ? showArchivedDocuments : showArchivedTasks}
                                onToggle={() => {
                                    const setShowArchived = isDocumentsView ? setShowArchivedDocuments : setShowArchivedTasks
                                    setShowArchived((prev) => !prev)
                                }}
                            />
                        </div>
                        <div
                            className={styles.project__createBtn}
                            onClick={isDocumentsView ? () => setIsUploadDocumentsOpen(true) : () => setIsTaskModalOpen(true)}
                        >
                            <PlusIcon />
                            {isDocumentsView ? 'Загрузить документ' : 'Создать задачу'}
                        </div>
                    </div>
                </div>
                <div className={styles.project__top}>
                    <OvalSwitcher
                        itemList={viewSwitcherItems}
                        activeId={activeView}
                        onChange={setActiveView}
                        defaultActiveId="board"
                    />
                </div>
                <div className={`${styles.project__content} ${activeView === 'board' ? styles.project__contentBoard : ''}`}>
                    {activeView === 'board' && <BoardView canPersistOrder={canMoveBoardTasks} getTasks={getTasks} onTaskUpdated={handleTaskUpdated} onTasksReordered={canMoveBoardTasks ? handleTasksReordered : undefined} onCreateTask={createTask} tasks={filteredTasks} allTasks={tasks} project={project} projectUsers={projectUsers}/>}
                    {activeView === 'list' && <ListView canPersistOrder={canPersistTaskOrder} tasks={filteredTasks} allTasks={tasks} getTasks={getTasks} onTaskUpdated={handleTaskUpdated} onTasksReordered={canPersistTaskOrder ? handleTasksReordered : undefined} project={project} projectUsers={projectUsers}/>}
                    {isDocumentsView && (
                        <DocumentsView
                            projectId={projectId || ''}
                            documents={documents}
                            currentUserIds={currentUserIds}
                            currentUserEmails={currentUserEmails}
                            isLoading={isDocumentsLoading}
                            showOnlyMyDocuments={showOnlyMyDocuments}
                            showArchivedDocuments={showArchivedDocuments}
                            highlightedDocumentId={linkedDocumentId}
                            onLinkCopied={handleLinkCopied}
                            onDocumentUpdated={handleDocumentUpdated}
                            onDocumentArchived={handleDocumentArchived}
                            onUploadClick={() => setIsUploadDocumentsOpen(true)}
                            onRefresh={getDocuments}
                        />
                    )}
                    <TaskModal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} createTask={createTask} project={project}/>
                    {linkedTask && (
                        <TaskModal
                            isOpen={Boolean(linkedTask)}
                            onClose={() => setLinkedTask(null)}
                            onEditCommitted={getTasks}
                            task={linkedTask}
                            targetCommentId={linkedCommentId}
                            createTask={saveLinkedTask}
                            project={project}
                        />
                    )}
                </div>
                <ProjectMembersPanel
                    isOpen={isMembersPanelOpen}
                    project={project ? { id: project.id } : null}
                    onClose={() => setIsMembersPanelOpen(false)}
                    selectionMembers={selectionMembers}
                    selectedMemberIds={selectedAssigneeIds}
                    onSelectionChange={updateSelectedAssigneeIds}
                />
            </div>
            <ProjectStatusModal
                isOpen={isStatusModalOpen}
                projectId={projectId}
                statuses={statusModalStatuses}
                onClose={() => setIsStatusModalOpen(false)}
                onSaved={(statuses) => setProject((prev: any) => prev ? { ...prev, statuses } : prev)}
            />
            <ProjectDeleteModal
                isOpen={isDeleteOpen}
                text={`Вы уверены, что хотите удалить проект «${project.name}»? Все задачи будут удалены.`}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={handleDeleteProject}
            />
            <ProjectDuplicateModal
                isOpen={isDuplicateOpen}
                defaultName={`${project.name || 'Проект'} (копия)`}
                defaultColor={project.color}
                onClose={() => setIsDuplicateOpen(false)}
                onConfirm={handleDuplicate}
            />
            <ShareProjectModal
                isOpen={isPrivacyOpen}
                project={project ? {
                    id: project.id,
                    name: project.name,
                    color: project.color,
                    is_public: project.is_public,
                } : null}
                onClose={() => setIsPrivacyOpen(false)}
                onSaved={({ is_public }) => {
                    setProject((currentProject: any) => currentProject ? { ...currentProject, is_public } : currentProject)
                }}
            />
            <UploadDocumentsModal
                isOpen={isUploadDocumentsOpen}
                projectId={projectId || ''}
                onClose={() => setIsUploadDocumentsOpen(false)}
                onUploaded={getDocuments}
                onSuccess={handleUploadSuccess}
                onError={showErrorNotification}
            />
            {successNotification && (
                <div className={styles.project__notification}>
                    <div className={styles.project__notificationBox}>
                        <div className={styles.project__notificationIcon}>
                            {successNotification.type === 'error' ? <ErrorNotificationIcon /> : <SuccessNotificationIcon />}
                        </div>
                        <div className={styles.project__notificationContent}>
                            <span className={styles.project__notificationTitle}>{successNotification.title}</span>
                            <span className={styles.project__notificationText}>{successNotification.text}</span>
                        </div>
                        {successNotification.action && (
                            <button
                                type="button"
                                className={styles.project__notificationAction}
                                onClick={successNotification.action.onClick}
                            >
                                {successNotification.action.label}
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        className={styles.project__notificationClose}
                        onClick={() => setSuccessNotification(null)}
                    >
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L7 7M7 1L1 7" stroke="#64617E" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
            )}
        </>
    )
}
